import { castToDecimal } from '@fruitful/fruitils';
import type { IncomeSplitConfig } from '@fruitful/income-split';
import {
    type MoneyMapMemberUpdateRequest,
    MoneyMapStatus,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import type { QueryClient } from '@tanstack/react-query';

import { buildQueryParams, requestBuilder } from '../../http/RequestBuilder';
import { getMoneyMapQueryKey } from './useGetMoneyMap';

export const writeMoneyMapMemberUpdateCaches = (
    queryClient: QueryClient,
    nextMoneyMap: MoneyMapWithRulesAndAccounts,
    sourceMoneyMapId: string,
) => {
    queryClient.setQueryData(['getActiveMoneyMap'], nextMoneyMap);
    queryClient.setQueryData(
        [getMoneyMapQueryKey({ id: nextMoneyMap.id })],
        nextMoneyMap,
    );

    const previewAndActiveKey = [
        `getMoneyMapsForMembership/${buildQueryParams({
            status: [MoneyMapStatus.PREVIEW, MoneyMapStatus.ACTIVE],
        })}`,
    ];

    queryClient.setQueryData(
        previewAndActiveKey,
        (old: MoneyMapWithRulesAndAccounts[] | undefined) => {
            const remaining = (old ?? []).filter(
                (map) =>
                    map.id !== sourceMoneyMapId && map.id !== nextMoneyMap.id,
            );
            return [nextMoneyMap, ...remaining];
        },
    );
};

export interface MoneyMapMemberUpdateResponse {
    moneyMap: MoneyMapWithRulesAndAccounts;
    /** Null when the membership has no income split to regenerate. */
    incomeSplit: IncomeSplitConfig | null;
}

export const useUpdateMoneyMapForMember = requestBuilder.mutation<
    MoneyMapMemberUpdateResponse,
    MoneyMapMemberUpdateRequest
>({
    endpoint: '/api/money-map/update',
    method: 'POST',
});

export const buildMoneyMapMemberUpdateRequest = ({
    moneyMap,
    incomeSplitSettings,
    expectedUpdatedAt,
}: {
    moneyMap: MoneyMapWithRulesAndAccounts;
    incomeSplitSettings: {
        onlyExecuteOnPaychecks: boolean;
        accountIds: string[];
    };
    expectedUpdatedAt?: string | null;
}): MoneyMapMemberUpdateRequest => {
    return {
        moneyMapId: moneyMap.id,
        monthlyIncomeCents: moneyMap.monthlyIncomeCents,
        ruleGroups: moneyMap.moneyMapRuleGroups.map((group) => ({
            id: group.id,
            name: group.name,
            description: group.description ?? null,
            priority: group.priority,
            // Prisma Decimal often arrives as a string; same coercion as Grove upsert.
            percentage: castToDecimal(group.percentage as number | string),
        })),
        rules: moneyMap.moneyMapRules.map((rule) => ({
            id: rule.id,
            moneyMapAccountId: rule.moneyMapAccountId,
            moneyMapRuleGroupId: rule.moneyMapRuleGroupId ?? null,
            type: rule.type,
            goalType: rule.goalType ?? null,
            description: rule.description,
            priority: rule.priority,
            routingRuleType: rule.routingRuleType ?? null,
            percentage:
                rule.percentage != null
                    ? castToDecimal(rule.percentage as number | string)
                    : null,
            fixedAmountCents: rule.fixedAmountCents ?? null,
            targetBalanceCents: rule.targetBalanceCents ?? null,
            monthlyContributionMaxCents:
                rule.monthlyContributionMaxCents ?? null,
            yearlyContributionMaxCents: rule.yearlyContributionMaxCents ?? null,
        })),
        incomeSplitSettings,
        ...(expectedUpdatedAt != null ? { expectedUpdatedAt } : {}),
    };
};
